import { Navigate, useParams } from 'react-router-dom'

import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import { getNewsArticleById, newsArticleLocaleFreePath } from '@/features/actualidad-hostelera/articles'
import { getLearnArticleBody } from '@/features/aprender/articleBodies'
import {
    getLearnArticleByLocaleSlug,
    learnArticleLocaleFreePath,
    localizeLearnArticle,
} from '@/features/aprender/articles'
import { authorPath, getAuthorByName } from '@/features/authors/authors'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES, SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl, localizePath } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { articleBreadcrumbListJsonLd, tipiPublisherJsonLd } from '@/shared/lib/siteSeo'
import { NewsletterSubscribe } from '@/features/newsletter/NewsletterSubscribe'
import { ArticleAiDisclosure } from '@/shared/ui/ArticleAiDisclosure'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import { SocialShareMeta } from '@/shared/ui/SocialShareMeta'
import '@/styles/food-cost-fonts.css'

import { NotFoundPage } from './NotFoundPage'

/** Articles moved from Aprender → Actualidad hostelera; keep SPA soft-nav working. */
const LEARN_TO_NEWS_ARTICLE_IDS: Record<string, string> = {
    'por-que-hosteleria-tarda-digitalizarse-control-costes': 'por-que-hosteleria-tarda-digitalizarse-control-costes',
    'why-hospitality-is-slow-to-digitize-cost-control': 'por-que-hosteleria-tarda-digitalizarse-control-costes',
}

export function LearnArticlePage() {
    const { slug = '' } = useParams<{ slug: string }>()
    const locale = useLandingLocale()

    const movedNewsId = LEARN_TO_NEWS_ARTICLE_IDS[slug]
    const movedNews = movedNewsId ? getNewsArticleById(movedNewsId) : undefined
    if (movedNews) {
        return <Navigate to={localizePath(newsArticleLocaleFreePath(movedNews), locale)} replace />
    }

    const definition = getLearnArticleByLocaleSlug(locale, slug)
    const article = definition ? localizeLearnArticle(definition, locale) : undefined
    const ArticleBody = definition ? getLearnArticleBody(definition.id) : undefined

    if (!definition || !article || !ArticleBody) {
        return <NotFoundPage />
    }

    const articlePath = learnArticleLocaleFreePath(definition)
    const canonicalUrl = absoluteLocalizedUrl(articlePath, locale)
    const imageUrl = `${SITE_ORIGIN}${article.imagePath}`
    const title = `${article.title} | Tipi`
    const description = article.description
    const authorProfile = getAuthorByName(article.authorName)
    const learnHubUrl = absoluteLocalizedUrl(ROUTES.learn, locale)
    const homeUrl = absoluteLocalizedUrl(ROUTES.home, locale)
    const learnHubName = locale === 'en' ? 'Learn' : 'Aprender'

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        image: [imageUrl],
        datePublished: article.publishedAt,
        dateModified: article.dateModified,
        inLanguage: locale === 'en' ? 'en-US' : 'es-ES',
        author: {
            '@type': 'Person',
            name: article.authorName,
            ...(authorProfile
                ? {
                      url: absoluteLocalizedUrl(authorPath(authorProfile), locale),
                      image: `${SITE_ORIGIN}${authorProfile.portraitSrc}`,
                  }
                : {}),
            worksFor: {
                '@type': 'Organization',
                name: 'Tipi',
                url: SITE_ORIGIN,
            },
        },
        publisher: tipiPublisherJsonLd(),
        mainEntityOfPage: canonicalUrl,
        articleSection: article.category,
    }

    const breadcrumbJsonLd = articleBreadcrumbListJsonLd([
        { name: 'Tipi', url: homeUrl },
        { name: learnHubName, url: learnHubUrl },
        { name: article.title, url: canonicalUrl },
    ])

    return (
        <div className="flex min-h-screen flex-col bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={articlePath} locale={locale} />
            <SocialShareMeta
                title={title}
                description={description}
                imageUrl={imageUrl}
                url={canonicalUrl}
                type='article'
            />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <meta property='article:published_time' content={article.publishedAt} />
                <meta property='article:modified_time' content={article.dateModified} />
                <meta property='article:author' content={article.authorName} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
                <script type='application/ld+json'>{JSON.stringify(breadcrumbJsonLd)}</script>
            </PageHelmet>

            <SubpageFoodCostHeader />

            <div className='flex-1 pt-20 md:pt-24'>
                <ArticleBody article={article} />
                <ArticleAiDisclosure />
                <NewsletterSubscribe
                    variant='article'
                    source={`aprender/${definition.id}`}
                    className='px-[clamp(18px,5vw,64px)]'
                />
            </div>

            <Footer landingPage='learn-article' className='border-[#F5EEE4]/10 bg-[#17130E]' />
        </div>
    )
}
